import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUserAuth } from "../../../../context/AuthContext";
import { usePortal } from "../../../../context/PortalContext";
import {
  buildDraftKey,
  getDraftUserId,
  mergeSessionDraft,
} from "../../../../helpers/SessionStorageHelpers";
import { trackBehaviourEvent } from "../../../../helpers/userBehaviourTrackingHelpers";
import { IClientData } from "../../../../models/ClientData";
import { ITokenPrefillResponse } from "../../../../models/LoggedOutFormData";
import { PartnerAssociationBlock } from "../../../../modules/common/menus/PartnerAssociationBlock";
import { SurveyJSForm } from "../../../../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../../../../modules/surveyjs_forms/surveyTheme";
import { titleLookup } from "../../../../modules/forms/Lookups";
import { personalDetailQuestions } from "../../../Campaigns/Schemas/LanderV2/shared";
import {
  LanderV2MarketingProps,
  LanderV2StepProps,
} from "../../../Campaigns/Schemas/LanderV2/types";
import { pushLanderFormSubmitEvent } from "../../../Utils/pushLanderFormSubmitEvent";
import { AuthHelpers } from "../../../../modules/auth/components/AuthHelpers";
import { useStickyBottomBar } from "../../../journey/useStickyBottomBar";
import { variantBAssets } from "../../../Utils/variantBAssets";
import {
  AppointmentBookingPayload,
  CalendlyBookingDetails,
  CalendlyDetailsForm,
} from "../CalendlyCustomWidget";
import { ComplianceNote } from "../shared/ComplianceNote";
import {
  BookingStepBase,
  fscsRibbedSrc,
  getQuoteLogo,
  RatingProof,
  tickGreenRibbedSrc,
  v1CallHref,
  v1CallNumber,
  Variant1ProcessSection,
  Variant1ProcessStep,
  Variant1ReviewsSection,
  Variant1SafeHandsRow,
  Variant1TalkSection,
} from "../annuitycalculator-v1/VariantV1";
import { LanderV2LoadingOverlay } from "../LanderV2LoadingOverlay";
import {
  ageText,
  buildIllustrationRequest,
  parseAmount,
  codMoney,
  CodIllustration,
  delayText,
  deriveResults,
  getCodInputs,
  getInputsKey,
  projectPot,
} from "./calculations";

const heroChecks = [
  {
    key: "real-rates",
    content: (
      <>
        Uses <strong>real annuity rates</strong> from leading UK providers
      </>
    ),
  },
  {
    key: "cost",
    content: (
      <>
        See the income you could <strong>miss</strong> by waiting
      </>
    ),
  },
  {
    key: "payback",
    content: (
      <>
        Find out how long it takes to <strong>earn it back</strong>
      </>
    ),
  },
  {
    key: "free",
    content: (
      <>
        <strong>100% FREE</strong> - it doesn't cost you a penny!
      </>
    ),
  },
];

export function CodMarketing(props: LanderV2MarketingProps) {
  const portal = usePortal();
  const { user } = useUserAuth();
  const tokenResolvedRef = useRef(false);
  // A resubmission (?tk=): the entry form fills itself from the token through the standard token prefill, as the annuity
  // calculator lander's does. The token's other details (contact, retirement date) are saved for the booking form. The original lead's link is kept aside
  // (never sent with the partials, so they build a normal session lead) until the booking's full submission, which
  // updates the original lead and turns the session lead into its duplicate submission.
  useEffect(() => {
    const token = getUrlToken();
    if (!token || tokenResolvedRef.current) return;
    tokenResolvedRef.current = true;

    void (async () => {
      try {
        const tokenData = (await portal.getPrefillDataFromToken?.(token)) as
          | ITokenPrefillResponse
          | undefined;
        if (!tokenData?.leadID || !tokenData.token) return;
        mergeSessionDraft(buildDraftKey(getDraftUserId(user)), {
          ...tokenToDraftAnswers(tokenData),
          "resubmission.tk": tokenData.token,
          "resubmission.transferToID": tokenData.leadID,
        });
      } catch {
        // No token details: the visitor just fills the forms in.
      }
    })();
  }, [portal, user]);

  const entryFormRef = useRef<HTMLDivElement>(null);
  useStickyBottomBar(entryFormRef, {
    enabled: true,
    barSelector: ".sd-body__navigation",
    widthSelector: ".sd-page",
  });

  return (
    <article className="pa-v1 cod">
      <header className="pa-v1-top">
        <img src={variantBAssets.penseLogo} alt="Pense" />
        <PartnerAssociationBlock className="pa-v1-associated" />
      </header>

      <section className="pa-v1-hero" aria-labelledby="cod-title">
        <div className="pa-v1-hero-copy">
          <h1 id="cod-title">{props.resolvedHeading}</h1>
          <ul className="pa-v1-checks">
            {heroChecks.map((check) => (
              <li key={check.key}>
                <span className="pa-v1-check-icon" aria-hidden="true">
                  <img src={tickGreenRibbedSrc} alt="" />
                  <i className="bi bi-check-lg"></i>
                </span>
                <span>{check.content}</span>
              </li>
            ))}
          </ul>
          <RatingProof />
        </div>

        <aside
          className="pa-v1-cta-card"
          aria-label="Calculate the cost of delay"
        >
          <img
            className="pa-v1-fscs-seal"
            src={fscsRibbedSrc}
            alt="FSCS protected"
          />
          <h2>{props.resolvedSubheading}</h2>
          <div ref={entryFormRef} className="lv2-sticky">
            <SurveyJSForm
              surveyFormId="cost-of-delay-calculator-details"
              pageSchemas={[props.entrySchemaPage]}
              initialData={props.initialData}
              clearDraftOnComplete={false}
              surveyProps={props.surveyProps}
              surveyTheme={lv2Theme}
              wrapperFormCssClass="lv2-survey pa-v1-entry-survey cod-entry-survey"
              surveyErrorTextColor="#ffffff"
              keepFormVisibleOnComplete
              suppressLoader
              onFirstInteraction={props.onFirstInteraction}
              onComplete={(data) => props.onQuoteSubmit(data ?? {})}
            />
          </div>
        </aside>
      </section>

      <Variant1ProcessSection steps={codProcessSteps} />
      <Variant1ReviewsSection />
      <Variant1SafeHandsRow
        ctaHeading="Ready to see what waiting could cost you?"
        ctaCopy={
          <>
            Find out in as little as <strong>2 minutes</strong>
          </>
        }
      />
      <Variant1TalkSection />
      <ComplianceNote />
    </article>
  );
}

// How the calculator works, in the shared "Getting the most from your pension" section (VLM3's illustrations).
const codProcessSteps: Variant1ProcessStep[] = [
  {
    title: "1. Tell us about your pension",
    copy: "Add your age and pension pot in a quick, secure form.",
    image: variantBAssets.processFeedback,
  },
  {
    title: "2. Choose how long you'd wait",
    copy: "Pick how many years you might delay and how your pot could grow in the meantime.",
    image: variantBAssets.processProfit,
  },
  {
    title: "3. See the cost of waiting",
    copy: "We compare real annuity rates today and later, showing the income you'd miss and how long it takes to earn back.",
    image: variantBAssets.processSchool,
  },
  {
    title: "4. Get FREE expert advice",
    copy: "Book a free call with a Retirement Specialist to decide the right time to take your income.",
    image: variantBAssets.processRelax,
  },
];

/** The landing page's green tick badge, for the "How we worked this out" list. */
function WorkingsTick() {
  return (
    <span className="pa-v1-check-icon" aria-hidden="true">
      <img src={tickGreenRibbedSrc} alt="" />
      <i className="bi bi-check-lg"></i>
    </span>
  );
}

export function CodResultsStep({
  lead,
  goToStep,
  journeyData,
  updateJourneyData,
}: LanderV2StepProps) {
  const portal = usePortal();
  const inputs = useMemo(() => getCodInputs(lead), [lead]);
  const inputsKey = inputs ? getInputsKey(inputs) : "";
  const delayedPot = inputs ? projectPot(inputs) : 0;
  const stored = journeyData.illustration as CodIllustration | undefined;
  const illustration = stored?.inputsKey === inputsKey ? stored : undefined;
  const results =
    inputs && illustration ? deriveResults(inputs, illustration) : null;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const requestedKeyRef = useRef("");
  const adviceButtonRef = useRef<HTMLDivElement>(null);
  useStickyBottomBar(adviceButtonRef, {
    enabled: Boolean(results),
    barSelector: ".lv2-sticky-bar",
    widthSelector: ".cod-results",
    resetKey: results ? inputsKey : "",
  });

  const requestQuotes = useCallback(async () => {
    if (!inputs || !portal.getUnsavedQuickQuotes) return;
    requestedKeyRef.current = inputsKey;
    setIsLoading(true);
    setError("");
    try {
      // Tied to the visitor's partial lead so iressquick doesn't add another.
      const leadLink: IClientData = { sessionId: lead.sessionId };
      const [current, delayed] = await Promise.all([
        portal.getUnsavedQuickQuotes({
          ...buildIllustrationRequest(inputs, inputs.age, inputs.pot),
          ...leadLink,
        }),
        portal.getUnsavedQuickQuotes({
          ...buildIllustrationRequest(
            inputs,
            inputs.age + inputs.delayYears,
            projectPot(inputs)
          ),
          ...leadLink,
        }),
      ]);
      if (typeof current === "number" || typeof delayed === "number") {
        trackBehaviourEvent("Lander Error", { stage: "quotes" });
        setError("Could not retrieve live annuity quotes.");
        return;
      }
      updateJourneyData({
        illustration: {
          inputsKey,
          current,
          delayed,
          quoteDate: new Date().toISOString(),
        } as CodIllustration,
      });
      trackBehaviourEvent("Quotes Received", {
        quote_count: String(current.length),
      });
    } finally {
      setIsLoading(false);
    }
  }, [inputs, inputsKey, lead.sessionId, portal, updateJourneyData]);

  useEffect(() => {
    if (!inputs || illustration || requestedKeyRef.current === inputsKey)
      return;
    void requestQuotes();
  }, [illustration, inputs, inputsKey, requestQuotes]);

  if (!inputs) return null;

  const onBook = () => goToStep("booking");
  const isWaiting = isLoading || (!illustration && !error);
  const noQuotes = Boolean(illustration && !results);
  const delayLabel = delayText(inputs.delayYears);
  const laterAgeLabel = ageText(inputs.age, inputs.delayYears);
  const paybackUnit = results?.roundedPaybackYears === 1 ? "year" : "years";

  return (
    <section className="cod-results">
      <div className="cod-results-headline">
        <h1>
          Waiting {delayLabel} could cost you{" "}
          <span className="cod-magenta">
            {results ? codMoney.format(results.missedIncome) : "…"}
          </span>{" "}
          in missed income
        </h1>
        <p className="ux2-text-body-lg">
          {results ? (
            <>
              That's <strong>{codMoney.format(results.incomeNow)}</strong> a
              year you won't receive while you wait.
            </>
          ) : (
            "We're calculating your potential cost of waiting using today's annuity rates."
          )}
        </p>
      </div>

      {/* VLM3's full-screen quote loader without the provider logos, over the results page so it stays mounted (the
          sticky button attaches on mount) */}
      {isWaiting ? (
        <LanderV2LoadingOverlay
          title="Nearly there"
          subtitle="We're just crunching the numbers"
          providerLogos={[]}
        />
      ) : null}

      {error && !isLoading ? (
        <div className="cod-results-error" role="alert">
          <div className="ux2-text-body-lg">
            <p>
              <strong>We couldn't get the quotes just now.</strong>
            </p>
            <p>{error}</p>
          </div>
          <button
            type="button"
            className="pa-v1-button pa-v1-button-magenta ux2-text-button"
            onClick={() => void requestQuotes()}
          >
            TRY AGAIN
          </button>
        </div>
      ) : null}

      {noQuotes ? (
        <section className="pa-v1-loading">
          <h1>We couldn't find quotes for these details</h1>
          <p className="ux2-text-body-lg">
            Our specialists can still find the right option for you.
          </p>
          <button
            type="button"
            className="pa-v1-button ux2-text-button"
            onClick={onBook}
          >
            BOOK AN APPOINTMENT
          </button>
        </section>
      ) : null}

      {results ? (
        <>
          <div className="cod-stat-cards">
            <article className="cod-stat-card">
              <h2 className="ux2-text-body-lg">
                <i className="bi bi-exclamation-circle" aria-hidden="true"></i>{" "}
                Income you miss
              </h2>
              <p className="cod-stat-value cod-magenta">
                {codMoney.format(results.missedIncome)}
              </p>
              <p className="ux2-text-body-lg">
                The annuity income you won't get during the {delayLabel} you
                wait.
              </p>
            </article>
            <article className="cod-stat-card cod-stat-card-dark">
              <h2 className="ux2-text-body-lg">
                <i className="bi bi-calendar-check" aria-hidden="true"></i> Time
                to earn it back
              </h2>
              <p className="cod-stat-value">
                {results.paybackYearsLabel === null
                  ? "—"
                  : `${results.paybackYearsLabel} ${paybackUnit}`}
              </p>
              <p className="ux2-text-body-lg">
                {results.paybackYears === null
                  ? "The delayed quote does not provide enough extra annual income to recover the income missed while waiting."
                  : `Even with the higher income, it takes about ${
                      results.paybackYearsLabel
                    } ${paybackUnit} to recover the ${codMoney.format(
                      results.missedIncome
                    )} you didn't take.`}
              </p>
            </article>
          </div>

          <section
            className="cod-workings"
            aria-labelledby="cod-workings-heading"
          >
            <h2 id="cod-workings-heading" className="ux2-text-heading">
              How we worked this out
            </h2>
            <ul className="ux2-text-body-lg">
              <li>
                <WorkingsTick />
                <span>
                  Pot today: <strong>{codMoney.format(inputs.pot)}</strong> (age{" "}
                  {inputs.age}).
                </span>
              </li>
              {inputs.age < 55 ? (
                <li>
                  <WorkingsTick />
                  <span>
                    We've used age 55 for your current quote, the minimum
                    pension age used for this illustration.
                  </span>
                </li>
              ) : null}
              <li>
                <WorkingsTick />
                <span>
                  Pot at age {laterAgeLabel}:{" "}
                  <strong>{codMoney.format(delayedPot)}</strong> -{" "}
                  {inputs.growthRate}% growth/year for {delayLabel}
                  {inputs.potMode === "contributions"
                    ? ` and ${codMoney.format(
                        inputs.monthlyAmount
                      )}/month contributions`
                    : ""}
                  .
                </span>
              </li>
              <li>
                <WorkingsTick />
                <span>
                  Annuity income if you wait:{" "}
                  <strong>{codMoney.format(results.incomeLater)} a year</strong>{" "}
                  from age {laterAgeLabel} -{" "}
                  {results.incomeIncrease >= 0
                    ? `${codMoney.format(results.incomeIncrease)} more`
                    : `${codMoney.format(
                        Math.abs(results.incomeIncrease)
                      )} less`}{" "}
                  than taking it now.
                </span>
              </li>
            </ul>
          </section>

          <section
            className="cod-advice"
            aria-label="Speak to a pension adviser"
          >
            <div>
              <h2 className="ux2-text-heading">
                Not sure whether to wait? Our Pension Advisors can help.
              </h2>
              <p className="ux2-text-body-lg">
                Our expert Pension Advisors will help you find the right
                product, at the very best rates.
              </p>
            </div>
            <div className="cod-advice-actions">
              <div ref={adviceButtonRef} className="lv2-sticky">
                <div className="lv2-sticky-bar">
                  <button
                    type="button"
                    className="pa-v1-button cod-advice-button ux2-text-button"
                    onClick={onBook}
                  >
                    GET FREE EXPERT ADVICE
                  </button>
                </div>
              </div>
              <span className="ux2-text-body-lg">
                Or call <strong>FREE</strong> on
              </span>
              <a className="ux2-text-phone" href={v1CallHref}>
                {v1CallNumber}
              </a>
            </div>
          </section>

          <section
            className="cod-best-quote"
            aria-labelledby="cod-best-quote-heading"
          >
            <div className="cod-best-quote-heading">
              <h2 id="cod-best-quote-heading" className="ux2-text-heading">
                Your best quote if you take it now
              </h2>
              <p className="ux2-text-body-lg">Real quote · available today</p>
            </div>
            <article className="cod-best-quote-card">
              <div className="cod-best-quote-provider">
                {getQuoteLogo(results.bestNow.providerName) ? (
                  <img
                    src={getQuoteLogo(results.bestNow.providerName)!}
                    alt={results.bestNow.providerName}
                  />
                ) : (
                  <strong>{results.bestNow.providerName}</strong>
                )}
              </div>
              <div className="cod-best-quote-figures">
                <strong className="ux2-text-heading">
                  {codMoney.format(results.incomeNow)}
                </strong>
                <span className="ux2-text-body-lg">
                  a year{inputs.age < 55 ? " from age 55" : ", starting now"}
                </span>
                <strong className="ux2-text-body-lg">
                  {codMoney.format(Number(results.bestNow.taxFreeCash ?? 0))}
                </strong>
                <span className="ux2-text-body-lg">Tax-free cash</span>
              </div>
              <div className="cod-best-quote-detail ux2-text-body-lg">
                <strong>{results.bestNow.providerName}</strong>
                <span>Single life, enhanced level</span>
              </div>
              <button
                type="button"
                className="pa-v1-button cod-take-quote-button ux2-text-button"
                onClick={onBook}
              >
                TAKE THIS QUOTE
              </button>
            </article>
          </section>

          <p className="cod-disclaimer ux2-text-body-lg">
            This is an indicative tool, not financial advice. Speak to an
            independent financial adviser to understand your options before
            making any decisions about your pension.
          </p>
          <p className="cod-disclaimer ux2-text-body-lg">
            The delayed quote uses annuity rates available on{" "}
            {new Date(illustration!.quoteDate).toLocaleDateString("en-GB")} for
            your projected pot and age. Rates may differ when you decide to take
            your pension.
          </p>
          {results.breakEvenAge !== null ? (
            <span className="visually-hidden">
              Estimated break-even age {results.breakEvenAge}.
            </span>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function getUrlToken() {
  return new URLSearchParams(window.location.search).get("tk")?.trim() || "";
}

/** The CRM's title (e.g. "MR", "Mrs.") as one of the booking form's title choices, or nothing if it isn't one. */
function toTitleChoice(title?: string) {
  const normalised = title?.trim().replace(/\.$/, "").toLowerCase();
  return titleLookup.find((option) => option.value.toLowerCase() === normalised)
    ?.value;
}

/** The token's lead details under the standard answer names (as the shared token prefill maps them); gaps left out. */
function tokenToDraftAnswers(tokenData: ITokenPrefillResponse) {
  const datePart = (value?: string) => value?.split("T")[0];
  const answers: Record<string, unknown> = {
    title: toTitleChoice(tokenData.title),
    lastName: tokenData.lastName,
    email: tokenData.email,
    primaryPhone: tokenData.mobilePhone,
    postcode: tokenData.postcode,
    dateOfBirth: datePart(tokenData.dateOfBirth),
    "annuity.pensionFund":
      parseAmount(tokenData.annuityTotalFundAmount) ?? undefined,
    "annuity.plannedRetirementDate": datePart(tokenData.retirementDate),
  };
  return Object.fromEntries(
    Object.entries(answers).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
}

function toRetirementParts(date?: string) {
  const match = /^(\d{4})-(\d{2})/.exec(date ?? "");
  return match ? { year: match[1], month: match[2] } : null;
}

const personalDetailQuestion = (name: string) =>
  personalDetailQuestions.find((question) => question.name === name)!;

// No first name here: the booking goes to Calendly under title and surname. Address comes last, followed by the
// hidden fields its lookup fills in. A postcode already known on arrival (a resubmission's token) means the address
// is known, so the search isn't shown.
function buildCodBookingDetailsForm(
  hasKnownPostcode: boolean
): CalendlyDetailsForm {
  const address = personalDetailQuestion("address");
  return {
    surveyFormId: "cost-of-delay-calculator-booking",
    onFirstInteraction: () => trackBehaviourEvent("Started Form Page 3"),
    schemaPage: {
      name: "codBookingDetails",
      elements: [
        personalDetailQuestion("title"),
        personalDetailQuestion("lastName"),
        personalDetailQuestion("email"),
        personalDetailQuestion("primaryPhone"),
        {
          type: "retirementdate",
          name: "annuity.plannedRetirementDate",
          title: "When do you plan to retire?",
          isRequired: true,
        },
        hasKnownPostcode ? { ...address, visible: false } : address,
        personalDetailQuestion("postcode"),
        personalDetailQuestion("city"),
        personalDetailQuestion("region"),
        personalDetailQuestion("country"),
      ],
    },
  };
}

const codBookingDisclaimer = (
  <p>
    By clicking "Calculate now" and supplying us with your information, you
    agree to our{" "}
    <a
      href="https://www.pense.co.uk/terms-of-use"
      target="_blank"
      rel="noopener noreferrer"
    >
      Terms of Use
    </a>{" "}
    and{" "}
    <a
      href="https://www.pense.co.uk/privacy-policy"
      target="_blank"
      rel="noopener noreferrer"
    >
      Privacy Policy
    </a>
    . You also confirm that you are happy for us to contact you regarding
    products and services which are relevant to your enquiry.
  </p>
);

// Page 3 is done once the booking's full lead has saved: the same "Lead With" events VLM3 sends for its full lead.
function trackLeadSaved() {
  trackBehaviourEvent("Completed Form Page 3");
  trackBehaviourEvent("Lead With Email");
  trackBehaviourEvent("Lead With Phone");
  trackBehaviourEvent("Lead With Address");
}

export function CodBookingStep(props: LanderV2StepProps) {
  const { lead, buildLead, saveLead, writeAnswers } = props;
  const portal = usePortal();
  const inputs = useMemo(() => getCodInputs(lead), [lead]);
  const [isReady, setIsReady] = useState(false);
  const preparedRef = useRef(false);
  // Decided on arrival, so choosing an address in the search doesn't then hide it.
  const [detailsForm] = useState(() =>
    buildCodBookingDetailsForm(Boolean(String(lead.postcode ?? "").trim()))
  );

  // Contact details (including a resubmission token's, saved on landing) are already in the draft. The retirement year
  // comes from the calculator's delay, keeping any month already known; the booking form mounts once this has run.
  useEffect(() => {
    if (preparedRef.current) return;
    preparedRef.current = true;

    const prepare = async () => {
      const prefill: Record<string, unknown> = {};

      if (inputs) {
        const year = String(new Date().getFullYear() + inputs.delayYears);
        const month =
          toRetirementParts(lead.annuity?.plannedRetirementDate)?.month ?? "";
        prefill.retirementYear = year;
        prefill.retirementMonth = month;
        prefill["annuity.plannedRetirementDate"] = month
          ? `${year}-${month}-01`
          : "";
      }

      if (Object.keys(prefill).length) writeAnswers(prefill);
      setIsReady(true);
    };

    void prepare();
  }, [inputs, lead, writeAnswers]);

  // Runs once Calendly has booked the slot, deciding the way the annuity calculator lander does: by whether a portal
  // account exists for the email. A resubmission's token goes with this full submission only, so the API updates the
  // original lead (fields, Resubmission milestone, journal note) and turns the session lead into its duplicate.
  async function submitLead(
    payload: AppointmentBookingPayload,
    details: CalendlyBookingDetails
  ): Promise<IClientData | null> {
    const bookedAt = new Date().toISOString();
    const contact = {
      title: details.title,
      lastName: details.lastName,
      email: details.email,
      primaryPhone: details.phone,
    };
    // Booked, so the lead (and any client insert) doesn't start the call workflow or ring the sales team.
    const resubmission = (
      lead as { resubmission?: { tk?: string; transferToID?: string } }
    ).resubmission;
    const booked: Partial<IClientData> = {
      portalGeneralAppointmentBookedLead: bookedAt,
      ...(resubmission?.tk && resubmission.transferToID
        ? { tk: resubmission.tk, transferToID: resubmission.transferToID }
        : {}),
    };

    if (await AuthHelpers.checkAccountExists(details.email)) {
      // An existing account isn't given a second client: the booking is saved on the lead as a repeat enquiry, and
      // the confirmation's START offers them a sign-in link. BookingStepBase has already tracked the booking and
      // checked the Calendly details.
      const leadSaved = await saveLead(contact, {
        ...booked,
        portalRepeatEnquiryDate: bookedAt,
        calendlyEventJSON: payload.calendlyEventJson,
      });
      if (!leadSaved) throw new Error("Lead could not be saved.");
      trackLeadSaved();
      pushLanderFormSubmitEvent(buildLead());
      return null;
    }

    // Otherwise the lead and quote submission VLM3 makes at its quote step: lead/update, then anon user +
    // client/insert + saved quick quotes + client/update. BookingStepBase then saves the booking onto the new client.
    if (!(await saveLead(contact, booked))) {
      throw new Error("Lead could not be saved.");
    }
    trackLeadSaved();

    const leadData: IClientData = { ...buildLead(), ...booked };
    const result = await portal.getNewAnonymousQuickQuotes?.(leadData);
    if (typeof result === "number" || result?.statusCode !== 200) {
      trackBehaviourEvent("Lander Error", { stage: "quotes" });
      throw new Error("Client and quotes could not be saved.");
    }
    pushLanderFormSubmitEvent(leadData);

    const {
      id: _id,
      transferToID: _transferToID,
      tk: _tk,
      ...client
    } = leadData;
    return client;
  }

  if (!isReady) {
    return (
      <section className="pa-v1-booking">
        <div
          className="cod-results-loading ux2-text-body-lg"
          role="status"
          aria-live="polite"
        >
          <span
            className="spinner-border spinner-border-sm"
            aria-hidden="true"
          ></span>
          Loading…
        </div>
      </section>
    );
  }

  return (
    <BookingStepBase
      {...props}
      detailsForm={detailsForm}
      stickyConfirmButton
      lockInputsWhileBooking
      disclaimer={codBookingDisclaimer}
      beforeClientUpdate={submitLead}
    />
  );
}
